import {ChangeDetectionStrategy, Component, Inject, Injectable, signal} from "@angular/core";
import {AgendaelementId, AvstemningMedResultatModell, AvstemningsstatusModell, GruppeId, GruppeModell, OpprettBeslutningSpesifikasjon} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {TranslateModule} from "@ngx-translate/core";
import {MatToolbar} from "@angular/material/toolbar";
import {MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogContent, MatDialogRef} from "@angular/material/dialog";
import {ReactiveFormsModule} from "@angular/forms";
import {MatIcon} from "@angular/material/icon";
import {BeslutningSkjemaKomponent} from "../../komponenter/beslutning-skjema/beslutning-skjema";
import {BeslutningDatatjeneste} from "felles-ng2/tjenester/datalager/BeslutningDatatjeneste";
import {MatButton, MatIconButton} from "@angular/material/button";
import {VisSpinnerDirektiv} from "web-ng2/delt/direktiver/button--visSpinner/button--visSpinner";
import {asynkArbeidPågårSignal, standardMinimumVarighetKonfig} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {AgendaelementModellDLM} from "felles-ng2/tjenester/datalager/SakDatatjeneste";
import {Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";

const debug = initDebugger('BeslutningOpprettDialogKomponent');

/**
 * Dialog for å opprette ny beslutning
 */
@Component({
    selector: 'beslutning-opprett-dialog',
    templateUrl: 'beslutning-opprett-dialog.html',
    styleUrl: 'beslutning-opprett-dialog.scss',
    host: {'data-testid': 'beslutning-skjema-dialog'},
    imports: [
        TranslateModule,
        MatToolbar,
        MatIcon,
        BeslutningSkjemaKomponent,
        MatDialogContent,
        ReactiveFormsModule,
        MatIconButton,
        VisSpinnerDirektiv,
        MatButton,
        MatDialogActions
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BeslutningOpprettDialogKomponent {

    static vis(dialog: MatDialog, sak: Pick<AgendaelementModellDLM, 'id' | 'eiendeGruppeId'>, erForslag: boolean): MatDialogRef<BeslutningOpprettDialogKomponent, BeslutningOpprettDialogRespons> {
        return BeslutningOpprettDialogKomponent.visDialog(dialog, {sakId: sak.id, gruppeId: sak.eiendeGruppeId, erForslag});
    }

    static visNyBeslutningBasertPåAvstemning(matDialog: MatDialog, avstemning: AvstemningMedResultatModell, avstemningsstatus: AvstemningsstatusModell, oversettelser: Oversettelser) {
        let dialog = BeslutningOpprettDialogKomponent.visDialog(matDialog, {sakId: avstemning.sakId, gruppeId: avstemning.eiendeGruppeId, erForslag: false});
        BeslutningSkjemaKomponent.populerSkjemaFraAvstemning(dialog.componentInstance.skjema, avstemning, avstemningsstatus, oversettelser);
        return dialog;
    }

    private static visDialog(dialog: MatDialog, dialogparametre: DialogParametre): MatDialogRef<BeslutningOpprettDialogKomponent, BeslutningOpprettDialogRespons> {
        return dialog.open(BeslutningOpprettDialogKomponent, {
            data: dialogparametre,
            disableClose: true,
            ariaLabelledBy: 'beslutning-opprett-dialog-tittel',
            minWidth: 500
        });
    }

    sakId: AgendaelementId; //kommer fra DialogParametre
    gruppeId: GruppeId; //kommer fra DialogParametre
    erForslag: boolean; //kommer fra DialogParametre

    skjema = BeslutningSkjemaKomponent.settFelterPåkrevdForNyBeslutning(BeslutningSkjemaKomponent.opprettSkjema());

    tilgjengeligeGrupper = signal<GruppeModell[]>([]);
    lagrer = asynkArbeidPågårSignal(standardMinimumVarighetKonfig());

    constructor(@Inject(MAT_DIALOG_DATA) parametre: DialogParametre,
                private dialogRef: MatDialogRef<BeslutningOpprettDialogKomponent, BeslutningOpprettDialogRespons>,
                private avhengigheter: BeslutningOpprettDialogKomponentAvhengigheter) {
        this.sakId = parametre.sakId;
        this.gruppeId = parametre.gruppeId;
        this.erForslag = parametre.erForslag;
    }

    async opprett() {
        await this.lagrer.utfør(() => this.avhengigheter.opprett(this.somSpesifikasjon()));
        this.dialogRef.close(true);
    }

    avbryt() {
        this.dialogRef.close(false);
    }

    // private hjelpemetoder

    private somSpesifikasjon(): OpprettBeslutningSpesifikasjon {
        return {
            ...BeslutningSkjemaKomponent.somSpesifikasjon(this.skjema),
            sakId: this.sakId,
            erForslag: this.erForslag,
            oppgaver: []
        }
    }
}

export type BeslutningOpprettDialogRespons = boolean;

export type DialogParametre = {
    sakId: AgendaelementId;
    gruppeId: GruppeId;
    erForslag: boolean;

};

@Injectable()
export class BeslutningOpprettDialogKomponentAvhengigheter {

    constructor(private beslutningDatatjeneste: BeslutningDatatjeneste) {
    }

    async opprett(spesifikasjon: OpprettBeslutningSpesifikasjon): Promise<void> {
        await this.beslutningDatatjeneste.opprett(spesifikasjon);
    }
}
